import { Controller } from "@hotwired/stimulus"

/**
 * CV Builder AI Suggestions controller.
 * Fetches suggestions from the server and displays them in a dropdown
 * for the user to accept or dismiss.
 *
 * Attach to a section wrapper. Expects:
 *   data-cv-builder-suggestions-url-value     — POST endpoint
 *   data-cv-builder-suggestions-type-value    — suggestion type
 *   data-cv-builder-suggestions-section-value — section name (for section_feedback)
 */
export default class extends Controller {
  static values = {
    url: String,
    type: String,
    section: { type: String, default: "" },
  }

  static targets = ["trigger", "panel", "results", "loading", "bulletText"]

  connect() {
    this._loading = false
  }

  async suggest(event) {
    event.preventDefault()
    if (this._loading) return

    const params = this._buildParams(event)
    if (!params) return

    const triggerType = event.currentTarget.dataset.suggestionType || this.typeValue
    const triggerTargetField = event.currentTarget.dataset.suggestionTargetField || ""

    this._showLoading()

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify(params),
      })

      if (response.status === 403) {
        this._showError("AI suggestion limit reached. Upgrade for more.")
        return
      }

      if (!response.ok) {
        this._showError("Could not get suggestions. Please try again.")
        return
      }

      const data = await response.json()
      this._renderSuggestions(data.suggestions, data.remaining, triggerType, triggerTargetField)
    } catch {
      this._showError("Connection error. Please try again.")
    }
  }

  apply(event) {
    const text = event.currentTarget.dataset.suggestionText
    const targetField = event.currentTarget.dataset.suggestionTarget
    if (!text || !targetField) return

    const field = this.element.querySelector(`[name="${targetField}"]`)
    if (field) {
      field.value = text
      field.dispatchEvent(new Event("input", { bubbles: true }))
    }

    this._hidePanel()
  }

  dismiss() {
    this._hidePanel()
  }

  // --- Private ---

  _buildParams(event) {
    const type = event.currentTarget.dataset.suggestionType || this.typeValue

    const params = { suggestion_type: type }

    if (type === "bullet_rewrite") {
      const bulletText = event.currentTarget.dataset.bulletText
      const roleContext = event.currentTarget.dataset.roleContext || ""
      if (!bulletText) return null
      params.text = bulletText
      params.role_context = roleContext
    } else if (type === "summary_rewrite") {
      params.role_context = this._getRoleContext()
    } else if (type === "skills_optimise") {
      // no extra params needed
    } else if (type === "section_feedback") {
      params.section_name = this.sectionValue
    }

    return params
  }

  _getRoleContext() {
    // Try to extract role context from the experience section
    const titleInput = document.querySelector('[name="experience[0].title"]')
    const companyInput = document.querySelector('[name="experience[0].company"]')
    const parts = []
    if (titleInput?.value) parts.push(titleInput.value)
    if (companyInput?.value) parts.push(`at ${companyInput.value}`)
    return parts.join(" ") || ""
  }

  _renderSuggestions(suggestions, remaining, type, targetField) {
    if (!this.hasResultsTarget) return

    let html = ""

    if (Array.isArray(suggestions)) {
      suggestions.forEach((item, i) => {
        const text = typeof item === "string" ? item : item.suggestion || JSON.stringify(item)
        const escaped = this._escapeHtml(text)
        const escapedAttr = this._escapeAttr(text)

        if (type === "bullet_rewrite" || type === "summary_rewrite") {
          html += `
            <div class="p-2 border border-gray-200 rounded hover:border-cyan-300 transition-colors">
              <p class="text-xs text-gray-700 mb-1">${escaped}</p>
              <button type="button" class="text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                      data-action="click->cv-builder-suggestions#apply"
                      data-suggestion-text="${escapedAttr}"
                      data-suggestion-target="${this._escapeAttr(targetField)}">
                Apply
              </button>
            </div>`
        } else if (type === "section_feedback") {
          const priority = item.priority || "medium"
          const dot = priority === "high" ? "text-red-500" : priority === "low" ? "text-gray-400" : "text-amber-500"
          html += `
            <div class="flex items-start gap-2 p-2">
              <span class="${dot} mt-0.5 text-xs">&#9679;</span>
              <span class="text-xs text-gray-700">${escaped}</span>
            </div>`
        } else {
          html += `<div class="p-2 text-xs text-gray-700">${escaped}</div>`
        }
      })
    } else if (typeof suggestions === "object") {
      // skills_optimise returns an object
      html = this._renderSkillsSuggestions(suggestions)
    }

    if (remaining !== null && remaining !== undefined) {
      html += `<div class="text-xs text-gray-400 p-2 border-t border-gray-100">${remaining} suggestions remaining this month</div>`
    }

    this.resultsTarget.innerHTML = html
    this._showPanel()
  }

  _renderSkillsSuggestions(data) {
    let html = ""

    if (data.add?.length) {
      html += '<div class="p-2"><p class="text-xs font-medium text-green-700 mb-1">Add:</p>'
      data.add.forEach((s) => {
        const text = typeof s === "string" ? s : `${s.skill} (${s.category})`
        html += `<span class="inline-block text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded mr-1 mb-1">${this._escapeHtml(text)}</span>`
      })
      html += "</div>"
    }

    if (data.remove?.length) {
      html += '<div class="p-2"><p class="text-xs font-medium text-red-700 mb-1">Consider removing:</p>'
      data.remove.forEach((s) => {
        html += `<span class="inline-block text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded mr-1 mb-1">${this._escapeHtml(String(s))}</span>`
      })
      html += "</div>"
    }

    return html
  }

  _showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
    this._showPanel()
    this._loading = true
  }

  _showPanel() {
    if (this.hasPanelTarget) this.panelTarget.classList.remove("hidden")
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    this._loading = false
  }

  _hidePanel() {
    if (this.hasPanelTarget) this.panelTarget.classList.add("hidden")
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
    this._loading = false
  }

  _showError(message) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `<div class="p-2 text-xs text-red-600">${this._escapeHtml(message)}</div>`
    }
    this._showPanel()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return str.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
